/**
 * ルートツリー（手組み。ファイルベース生成は使わない — 05-ui D-5）。
 *
 * 構成:
 *   __root__
 *     ├─ /login            … シェルの外。未認証でも描ける唯一の画面
 *     └─ (pathless: 'app')  … 認証ガード + <AppShell>
 *          ├─ /            … ダッシュボード
 *          ├─ /nodes …     … 各画面
 *          └─ …
 *
 * データ取得は TanStack Query 単独で行い、Router の `loader` は使わない（D-6）。
 * ローダーに載せると「再取得・ポーリング・楽観更新」を二重に管理することになる。
 */
import * as React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { APP_SHELL_ROUTER_SCROLL_OPTIONS, Button, EmptyState } from '@mail-sv/ui';
import { AppLayout } from './components/app-layout.js';
import { authStore } from './stores/auth.js';
import {
  aiSearchSchema,
  announcementsSearchSchema,
  auditSearchSchema,
  backupSearchSchema,
  billingSearchSchema,
  breakGlassSearchSchema,
  campaignsSearchSchema,
  certificatesSearchSchema,
  deliverabilitySearchSchema,
  domainDetailSearchSchema,
  domainsSearchSchema,
  incidentsSearchSchema,
  jobsSearchSchema,
  loginSearchSchema,
  mailboxesSearchSchema,
  nodeDetailSearchSchema,
  nodesSearchSchema,
  orgDetailSearchSchema,
  orgsSearchSchema,
  searchValidator,
  privacySearchSchema,
  privateAddressesSearchSchema,
  securitySearchSchema,
  settingsSearchSchema,
  slaCreditsSearchSchema,
  subscriptionsSearchSchema,
  supportSearchSchema,
  updatesSearchSchema,
  webmailSearchSchema,
} from './lib/search.js';

import { LoginPage } from './routes/login.js';
import { DashboardPage } from './routes/dashboard.js';
import { NodesPage } from './routes/nodes.js';
import { NodeDetailPage } from './routes/node-detail.js';
import { OrganizationsPage } from './routes/organizations.js';
import { OrganizationDetailPage } from './routes/organization-detail.js';
import { DomainsPage } from './routes/domains.js';
import { DomainDetailPage } from './routes/domain-detail.js';
import { MailboxesPage } from './routes/mailboxes.js';
import { JobsPage } from './routes/jobs.js';
import { JobDetailPage } from './routes/job-detail.js';
import { DeliverabilityPage } from './routes/deliverability.js';
import { BackupPage } from './routes/backup.js';
import { NodePoolsPage } from './routes/node-pools.js';
import { CertificatesPage } from './routes/certificates.js';
import { WebmailPage } from './routes/webmail.js';
import { CampaignsPage } from './routes/campaigns.js';
import { CampaignDetailPage } from './routes/campaign-detail.js';
import { BreakGlassPage } from './routes/break-glass.js';
import { AiPage } from './routes/ai.js';
import { IncidentsPage } from './routes/incidents.js';
import { SupportPage } from './routes/support.js';
import { AnnouncementsPage } from './routes/announcements.js';
import { SlaCreditsPage } from './routes/sla-credits.js';
import { UpdatesPage } from './routes/updates.js';
import { RolloutDetailPage } from './routes/rollout-detail.js';
import { PrivacyPage } from './routes/privacy.js';
import { PrivateAddressesPage } from './routes/private-addresses.js';
import { SecurityPage } from './routes/security.js';
import { AuditPage } from './routes/audit.js';
import { PlansPage } from './routes/plans.js';
import { SubscriptionsPage } from './routes/subscriptions.js';
import { InvoicesPage } from './routes/invoices.js';
import { InvoiceDetailPage } from './routes/invoice-detail.js';
import { StaffPage } from './routes/staff.js';
import { SettingsPage } from './routes/settings.js';
import { PrintSetupSheetPage } from './routes/print-setup-sheet.js';

/* ------------------------------------------------------------------ root */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

function NotFound(): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <EmptyState
        title="ページが見つかりません"
        description="URL が変更されたか、削除された可能性があります。"
        action={
          <Button asChild variant="outline">
            <Link to="/">ダッシュボードへ戻る</Link>
          </Button>
        }
      />
    </div>
  );
}

/* ----------------------------------------------------------------- login */

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: searchValidator(loginSearchSchema),
  component: LoginPage,
});

/* ------------------------------------------------------- 認証済みレイアウト */

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    // 起動時に `bootstrapSession()` を await 済みなので、ここは同期判定でよい。
    // 非同期にすると「一瞬ダッシュボードが見えてからログインへ飛ぶ」ちらつきが出る。
    if (!authStore.isAuthenticated()) {
      throw redirect({ to: '/login', search: { redirect: location.href }, replace: true });
    }
  },
  component: AppLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
});

const nodesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/nodes',
  validateSearch: searchValidator(nodesSearchSchema),
  component: NodesPage,
});

const nodeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/nodes/$nodeId',
  validateSearch: searchValidator(nodeDetailSearchSchema),
  component: NodeDetailPage,
});

const organizationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organizations',
  validateSearch: searchValidator(orgsSearchSchema),
  component: OrganizationsPage,
});

const organizationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organizations/$orgId',
  validateSearch: searchValidator(orgDetailSearchSchema),
  component: OrganizationDetailPage,
});

const domainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/domains',
  validateSearch: searchValidator(domainsSearchSchema),
  component: DomainsPage,
});

const domainDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/domains/$domainId',
  validateSearch: searchValidator(domainDetailSearchSchema),
  component: DomainDetailPage,
});

const mailboxesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mailboxes',
  validateSearch: searchValidator(mailboxesSearchSchema),
  component: MailboxesPage,
});

const jobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/jobs',
  validateSearch: searchValidator(jobsSearchSchema),
  component: JobsPage,
});

const jobDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/jobs/$jobId',
  component: JobDetailPage,
});

const deliverabilityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/deliverability',
  validateSearch: searchValidator(deliverabilitySearchSchema),
  component: DeliverabilityPage,
});

const backupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/backup',
  validateSearch: searchValidator(backupSearchSchema),
  component: BackupPage,
});

const nodePoolsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/node-pools',
  component: NodePoolsPage,
});

const certificatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/certificates',
  validateSearch: searchValidator(certificatesSearchSchema),
  component: CertificatesPage,
});

const webmailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/webmail',
  validateSearch: searchValidator(webmailSearchSchema),
  component: WebmailPage,
});

const campaignsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns',
  validateSearch: searchValidator(campaignsSearchSchema),
  component: CampaignsPage,
});

/**
 * 個別配信の詳細。**緊急停止が効いたかを確かめる画面**なので、
 * 一覧（`/campaigns`）から必ず辿れるようにしておく。
 */
const campaignDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/$campaignId',
  component: CampaignDetailPage,
});

const breakGlassRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/break-glass',
  validateSearch: searchValidator(breakGlassSearchSchema),
  component: BreakGlassPage,
});

const aiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ai',
  validateSearch: searchValidator(aiSearchSchema),
  component: AiPage,
});

const incidentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/incidents',
  validateSearch: searchValidator(incidentsSearchSchema),
  component: IncidentsPage,
});

const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support',
  validateSearch: searchValidator(supportSearchSchema),
  component: SupportPage,
});

const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/announcements',
  validateSearch: searchValidator(announcementsSearchSchema),
  component: AnnouncementsPage,
});

const slaCreditsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sla-credits',
  validateSearch: searchValidator(slaCreditsSearchSchema),
  component: SlaCreditsPage,
});

const updatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/updates',
  validateSearch: searchValidator(updatesSearchSchema),
  component: UpdatesPage,
});

const rolloutDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/updates/rollouts/$rolloutId',
  component: RolloutDetailPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/privacy',
  validateSearch: searchValidator(privacySearchSchema),
  component: PrivacyPage,
});

/**
 * 使い捨てアドレスの濫用監視。**`/privacy` とは別画面**にしている。
 * あちらは画像プロキシ（Webメールの表示）の設定で、こちらは契約者の
 * 発行速度を見張る監視系。読む頻度も権限の意味合いも違う。
 */
const privateAddressesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/private-addresses',
  validateSearch: searchValidator(privateAddressesSearchSchema),
  component: PrivateAddressesPage,
});

const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security',
  validateSearch: searchValidator(securitySearchSchema),
  component: SecurityPage,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  validateSearch: searchValidator(auditSearchSchema),
  component: AuditPage,
});

const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/plans',
  component: PlansPage,
});

const subscriptionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/subscriptions',
  validateSearch: searchValidator(subscriptionsSearchSchema),
  component: SubscriptionsPage,
});

const invoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/invoices',
  validateSearch: searchValidator(billingSearchSchema),
  component: InvoicesPage,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/invoices/$invoiceId',
  component: InvoiceDetailPage,
});

const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff',
  component: StaffPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  validateSearch: searchValidator(settingsSearchSchema),
  component: SettingsPage,
});

/* ------------------------------------------------------------- 印刷用の画面 */

/**
 * 印刷用の画面。**認証は要るがアプリのレイアウト（サイドナビ・ヘッダ）は着けない。**
 *
 * 設定情報シートは契約者に渡す紙で、画面の一部ではない。`appRoute` の下に
 * 置くと、ナビゲーションを消す `@media print` を全画面ぶん書くことになり、
 * 1 か所直すたびに他の画面の印刷が壊れる。ガードは `appRoute` と同じ。
 */
const printRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'print',
  beforeLoad: ({ location }) => {
    if (!authStore.isAuthenticated()) {
      throw redirect({ to: '/login', search: { redirect: location.href }, replace: true });
    }
  },
  component: () => <Outlet />,
});

const printSetupSheetRoute = createRoute({
  getParentRoute: () => printRoute,
  path: '/print/mail-setup',
  // `ids` はカンマ区切りのメールボックス ID。画面側で分解と重複除去を行う。
  validateSearch: (input: Record<string, unknown>): { ids?: string } =>
    typeof input.ids === 'string' ? { ids: input.ids } : {},
  component: PrintSetupSheetPage,
});

/* ------------------------------------------------------------------ tree */

const routeTree = rootRoute.addChildren([
  loginRoute,
  printRoute.addChildren([printSetupSheetRoute]),
  appRoute.addChildren([
    dashboardRoute,
    nodesRoute,
    nodeDetailRoute,
    organizationsRoute,
    organizationDetailRoute,
    domainsRoute,
    domainDetailRoute,
    mailboxesRoute,
    jobsRoute,
    jobDetailRoute,
    deliverabilityRoute,
    backupRoute,
    nodePoolsRoute,
    certificatesRoute,
    webmailRoute,
    campaignsRoute,
    campaignDetailRoute,
    breakGlassRoute,
    aiRoute,
    incidentsRoute,
    supportRoute,
    announcementsRoute,
    slaCreditsRoute,
    updatesRoute,
    rolloutDetailRoute,
    privacyRoute,
    privateAddressesRoute,
    securityRoute,
    auditRoute,
    plansRoute,
    subscriptionsRoute,
    invoicesRoute,
    invoiceDetailRoute,
    staffRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // 一覧行のホバーで詳細を先読みする。運用画面はクリック前に迷う時間が長い。
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  // スクロールするのは window ではなく AppShell の main。新しい画面では main も先頭へ戻す。
  ...APP_SHELL_ROUTER_SCROLL_OPTIONS,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
